import React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { SummaryCardData, STATUS_COLORS } from './types';

interface ReportSummaryCardProps extends SummaryCardData {
  onViewAll?: (card: SummaryCardData) => void;
  className?: string;
}

const TrendIcon: React.FC<{ trend: 'up' | 'down' | 'neutral' }> = ({ trend }) => {
  if (trend === 'up') return <TrendingUp className="h-5 w-5 text-emerald-600" />;
  if (trend === 'down') return <TrendingDown className="h-5 w-5 text-rose-600" />;
  return null;
};

const ReportSummaryCard: React.FC<ReportSummaryCardProps> = ({
  title,
  subtitle,
  value,
  target,
  trend = 'neutral',
  trendValue,
  status = 'default',
  onViewAll,
  className = '',
  ...props
}) => {
  const progressValue = (value && target) ? (value / target) * 100 : 0;
  const statusColor = STATUS_COLORS[status] || STATUS_COLORS.default;

  const handleViewAllClick = () => {
    onViewAll?.({ title, subtitle, value, target, trend, trendValue, status, ...props });
  };

  return (
    <div className={cn('bg-white rounded-2xl p-6 shadow-sm border border-gray-200 flex flex-col', className)}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900">{title}</h3>
          {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
        </div>
      </div>

      <div className="space-y-3 flex-grow">
        {typeof value === 'number' && (
            <p className="text-3xl font-bold text-gray-900">{value.toLocaleString()}</p>
        )}
        {trendValue && (
            <div className="flex items-center gap-2">
                <TrendIcon trend={trend} />
                <span className={cn('text-sm font-medium', trend === 'up' ? 'text-emerald-600' : 'text-rose-600')}>{trendValue}</span>
            </div>
        )}
        {typeof value === 'number' && typeof target === 'number' && (
            <div className="space-y-2 pt-2">
                <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Progress</span>
                    <span className="font-medium text-gray-800">{Math.round(progressValue)}%</span>
                </div>
                <Progress value={progressValue} className="h-2" indicatorClassName={statusColor.bg} />
            </div>
        )}
      </div>

      {onViewAll && (
        <div className="mt-4 pt-4 border-t border-gray-100">
            <Button variant="link" className="p-0 h-auto text-sky-600 hover:text-sky-700 text-sm font-medium" onClick={handleViewAllClick}>
                View All
            </Button>
        </div>
      )}
    </div>
  );
};

ReportSummaryCard.displayName = 'ReportSummaryCard';

export { ReportSummaryCard };
